/**
 * Fill a calendar year of monthly bars and optionally stack a prediction remainder.
 */

export interface MonthRow {
  monthKey: string;
  monthLabel: string;
}

export function monthsOfYear(year: number): MonthRow[] {
  const rows: MonthRow[] = [];
  for (let monthIndex = 0; monthIndex < 12; monthIndex += 1) {
    const d = new Date(year, monthIndex, 1);
    const m = d.getMonth() + 1;
    rows.push({
      monthKey: `${d.getFullYear()}-${String(m).padStart(2, '0')}`,
      monthLabel: d.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }),
    });
  }
  return rows;
}

export function seriesToMap(series: Array<{ month: string; value: number | null }> | null | undefined): Map<string, number> {
  const map = new Map<string, number>();
  for (const p of series || []) {
    if (!p.month) continue;
    const v = typeof p.value === 'number' ? p.value : parseFloat(String(p.value));
    if (!Number.isNaN(v)) map.set(p.month, v);
  }
  return map;
}

export interface PredictionRemainder {
  month: string;
  predictedRemaining: number | string;
}

/** 12 months keyed by metric slug; used for grouped bars (joined vs left). */
export function buildGroupedMonthlyRows(
  metrics: Array<{ slug: string; series: Array<{ month: string; value: number | null }> }>,
  year: number,
): Array<MonthRow & Record<string, string | number>> {
  const maps = metrics.map((m) => ({ slug: m.slug, map: seriesToMap(m.series) }));
  return monthsOfYear(year).map((row) => {
    const out: MonthRow & Record<string, string | number> = { ...row };
    for (const { slug, map } of maps) {
      out[slug] = map.get(row.monthKey) ?? 0;
    }
    return out;
  });
}
export interface StackedPredictionRow extends MonthRow {
  value: number;
  predicted: number;
  predictedTotal: number;
}

/** Collected value plus a ghost "still to come" segment on the predicted month. */
export function buildStackedPredictionRows(
  series: Array<{ month: string; value: number | null }> | null,
  year: number,
  prediction: PredictionRemainder | null,
): StackedPredictionRow[] {
  const map = seriesToMap(series);
  const predictedRemaining = prediction ? parseFloat(String(prediction.predictedRemaining)) : NaN;
  return monthsOfYear(year).map((row) => {
    const value = map.get(row.monthKey) ?? 0;
    const isPredictedMonth = !!prediction && prediction.month === row.monthKey && !Number.isNaN(predictedRemaining);
    const predicted = isPredictedMonth ? Math.max(predictedRemaining, 0) : 0;
    return {
      ...row,
      value,
      predicted,
      predictedTotal: isPredictedMonth ? value + predicted : value,
    };
  });
}

export interface RateSeriesPoint {
  month: string;
  value: number | null;
  cost?: number | null;
  spend?: number;
  conversions?: number;
  ads?: number;
}

export interface RateCell {
  /** Results per $100; null when the month had no ads. */
  rate: number | null;
  cost: number | null;
  conversions: number;
}

export interface RateRow extends MonthRow {
  /** Sales ads that month; shared by every series (same ads, different result). */
  hasAds: boolean;
  spend: number;
  ads: number;
  cells: Record<string, RateCell>;
  /** Bar heights by metric slug (0 when the month had no ads). */
  [bar: string]: unknown;
}

/** 12 months of results-per-$100 bars, one key per metric slug. */
export function buildRateRows(
  metrics: Array<{ slug: string; series: RateSeriesPoint[] | null | undefined }>,
  year: number,
): RateRow[] {
  const bySlug = metrics.map((m) => ({
    slug: m.slug,
    byMonth: new Map((m.series || []).map((p) => [p.month, p])),
  }));

  return monthsOfYear(year).map((row) => {
    const out: RateRow = { ...row, hasAds: false, spend: 0, ads: 0, cells: {} };
    for (const { slug, byMonth } of bySlug) {
      const point = byMonth.get(row.monthKey);
      const rate = point && point.value != null ? Number(point.value) : null;
      if (point) {
        out.hasAds = true;
        out.spend = Number(point.spend ?? 0);
        out.ads = Number(point.ads ?? 0);
      }
      out.cells[slug] = {
        rate,
        cost: point?.cost != null ? Number(point.cost) : null,
        conversions: Number(point?.conversions ?? 0),
      };
      out[slug] = rate ?? 0;
    }
    return out;
  });
}

export interface BreakdownRow extends MonthRow {
  total: number;
  /** Bar heights by key (status or source); 0 when absent. */
  [key: string]: string | number;
}

/**
 * 12 months of stacked status counts. Returns the rows plus the keys that
 * occur in the year, in `order` (unknown keys, e.g. `none`, go last), so the
 * chart only draws segments that exist.
 */
export function buildStatusStackRows(
  byStatus: Record<string, Record<string, number>> | null | undefined,
  year: number,
  order: string[],
): { rows: BreakdownRow[]; keys: string[] } {
  const present = new Set<string>();
  for (const counts of Object.values(byStatus || {})) {
    for (const [k, n] of Object.entries(counts)) if (n > 0) present.add(k);
  }
  const keys = [
    ...order.filter((k) => present.has(k)),
    ...[...present].filter((k) => !order.includes(k)).sort(),
  ];
  const rows = monthsOfYear(year).map((row) => {
    const counts = byStatus?.[row.monthKey] ?? {};
    const out: BreakdownRow = { ...row, total: 0 };
    for (const k of keys) {
      out[k] = counts[k] ?? 0;
      out.total += counts[k] ?? 0;
    }
    return out;
  });
  return { rows, keys };
}

export interface SourceCell {
  total: number;
  paid: number;
  open: number;
}

export interface SourceRow extends MonthRow {
  cells: Record<string, SourceCell>;
  /** `<source>`: consultation count; `<source>__rate`: paid % (null when no consultations). */
  [key: string]: unknown;
}

export const rateKey = (source: string) => `${source}__rate`;

/** 12 months of per-source counts and paid rates; keys limited to sources present in the year. */
export function buildSourceRows(
  bySource: Record<string, Record<string, SourceCell>> | null | undefined,
  year: number,
  order: string[],
): { rows: SourceRow[]; keys: string[] } {
  const present = new Set<string>();
  for (const cells of Object.values(bySource || {})) {
    for (const [k, c] of Object.entries(cells)) if (c.total > 0) present.add(k);
  }
  const keys = [
    ...order.filter((k) => present.has(k)),
    ...[...present].filter((k) => !order.includes(k)).sort(),
  ];
  const rows = monthsOfYear(year).map((row) => {
    const cells = bySource?.[row.monthKey] ?? {};
    const out: SourceRow = { ...row, cells: {} };
    for (const k of keys) {
      const cell = cells[k] ?? { total: 0, paid: 0, open: 0 };
      out.cells[k] = cell;
      out[k] = cell.total;
      out[rateKey(k)] = cell.total > 0 ? (cell.paid / cell.total) * 100 : null;
    }
    return out;
  });
  return { rows, keys };
}

export interface SpendRow extends MonthRow {
  /** Spend up to and including the current month; null after it. */
  actual: number | null;
  /** Planned spend from the current month on (overlaps at the current month so the line joins). */
  planned: number | null;
}

/**
 * 12 months of ad spend. No spend is a real $0. Months after `currentMonth`
 * are planned budget, drawn as a separate dashed series.
 */
export function buildSpendRows(
  series: Array<{ month: string; value: number | null }> | null | undefined,
  year: number,
  currentMonth: string | null | undefined,
): SpendRow[] {
  const map = seriesToMap(series);
  return monthsOfYear(year).map((row) => {
    const value = map.get(row.monthKey) ?? 0;
    const isPastOrCurrent = !currentMonth || row.monthKey <= currentMonth;
    const isCurrentOrFuture = !!currentMonth && row.monthKey >= currentMonth;
    return {
      ...row,
      actual: isPastOrCurrent ? value : null,
      planned: isCurrentOrFuture ? value : null,
    };
  });
}
